 import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_RIDER_API_URL;
const VEHICLE_SERVICE_URL = import.meta.env.VITE_API_GATEWAY_URL || 'http://localhost:8000'; // Update with actual vehicle service URL
interface SDConfig {
  id: string;
  vehicleModelId: string;
  hubName: string | null;
  baseSecurityDeposit: number;
  minimumSecurityDeposit: number;
  depreciationPerMonth: number;
  maxDepreciationPercent: number;
  reductionPercentagePerYear: number;
  useMultiplier: boolean;
  rentalCostMultiplier: number;
  modelName: string;
  brandName: string;
  category: string | null;
  batteryConfig: string | null;
  city: string | null;
  fixedDailyRent: number | null;
  isActive: boolean;
  effectiveFrom: string;
  effectiveUntil: string | null;
  notes: string | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  lastModifiedBy: string | null;
}

interface SDCalculation {
  baseSecurityDeposit: number;
  depreciationAmount: number;
  depreciationPercentage: number;
  calculatedSD: number;
  minimumSD: number;
  finalSD: number;
  calculationMethod: string;
}

interface VehicleModel {
  id: string;
  name: string;
  displayName: string;
  category: string | null;
  segment: string | null;
  vehicleType: string;
  oemId: string | null;
  oem: {
    id: string;
    name: string;
    displayName: string;
  } | null;
}

interface City {
  id: string;
  name: string;
  displayName: string;
  code?: string;
  state?: string;
  country?: string;
}

const SecurityDepositConfigManagement: React.FC = () => {
  const [configs, setConfigs] = useState<SDConfig[]>([]);
  const [vehicleModels, setVehicleModels] = useState<VehicleModel[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SDConfig | null>(null);
  const [calculation, setCalculation] = useState<SDCalculation | null>(null);

  // Filter state
  const [filterCity, setFilterCity] = useState<string>('');
  const [filterModel, setFilterModel] = useState<string>('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Form state
  const [formData, setFormData] = useState<{
    vehicleModelId: string;
    modelName: string;
    brandName: string;
    category: string;
    batteryConfig: string;
    cityId: string;
    city: string; // This will store the city name
    hubName: string;
    fixedDailyRent: number | '';
    reductionPercentagePerYear: number;
    baseSecurityDeposit: number | '';
    minimumSecurityDeposit: number | '';
    depreciationPerMonth: number;
    maxDepreciationPercent: number;
    useMultiplier: boolean;
    rentalCostMultiplier: number;
    notes: string;
    imageUrl: string;

  }>({
    vehicleModelId: '',
    modelName: '',
    brandName: '',
    category: '',
    batteryConfig: '',
    cityId: '',
    city: '',
    hubName: '',
    fixedDailyRent: 0,
    reductionPercentagePerYear: 0.05,
    baseSecurityDeposit: 10000,
    minimumSecurityDeposit: 5000,
    depreciationPerMonth: 0.02,
    maxDepreciationPercent: 0.30,
    useMultiplier: true,
    rentalCostMultiplier: 2.0,
    notes: '',
    imageUrl: '',
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    vehicleModelId: '',
    modelName: '',
    brandName: '',
    city: '',
    hubName: '',
    baseSecurityDeposit: '',
    minimumSecurityDeposit: '',
    depreciationPerMonth: '',
    maxDepreciationPercent: '',
    rentalCostMultiplier: '',
    batteryConfig: '',
    imageUrl: '',
  });

  // Calculator state
  const [calculatorData, setCalculatorData] = useState({
    vehicleModelId: '',
    vehicleAge: 0,
    reductionPercentage: 5,
  });

  useEffect(() => {
    fetchConfigs();
  }, [page, rowsPerPage, filterCity, filterModel]);

  useEffect(() => {
    fetchVehicleModels();
    fetchCities();
  }, []);


  const fetchVehicleModels = async () => {
  setLoadingModels(true);
  try {
    const response = await axios.get(`${VEHICLE_SERVICE_URL}/api/v1/vehicle-models/`, {
      timeout: 5000
    });

    const raw = response.data;

const list =
  Array.isArray(raw?.rows)
    ? raw.rows
    : Array.isArray(raw?.data?.rows)
    ? raw.data.rows
    : Array.isArray(raw?.data)
    ? raw.data
    : [];

setVehicleModels(list);
  } catch {
    setVehicleModels([]);
  } finally {
    setLoadingModels(false);
  }
};



  const fetchCities = async () => {
  setLoadingCities(true);
  try {
    // note: include protocol so axios doesn't treat this as a relative path
    const response = await axios.get(`${VEHICLE_SERVICE_URL}/api/cities/operational`);

    const raw = response.data;

    const list =
      Array.isArray(raw?.rows)
        ? raw.rows
        : Array.isArray(raw?.data?.rows)
        ? raw.data.rows
        : Array.isArray(raw?.data)
        ? raw.data
        : [];

    setCities(list);
  } catch (err: any) {
    console.error('Failed to fetch cities', err);
    setCities([]);
  } finally {
    setLoadingCities(false);
  }
};

  const checkDuplicateConfig = (
    vehicleModelId: string,
    cityName: string,
    hubName: string,
    batteryConfig: string,
    selectedConfigId?: string
  ) => {
    if (!vehicleModelId || !cityName || !hubName || !batteryConfig) {
      setFormErrors((prev) => ({ ...prev, vehicleModelId: '' }));
      return;
    }

    const existingConfig = configs.find(
      (config) =>
        config.id !== selectedConfigId &&
        config.vehicleModelId === vehicleModelId &&
        config.city === cityName &&
        config.hubName === hubName &&
        config.batteryConfig === batteryConfig
    );

    if (existingConfig) {
      const locationLabel = existingConfig.hubName
        ? `${cityName} / ${existingConfig.hubName}`
        : cityName;
      setFormErrors((prev) => ({
        ...prev,
        vehicleModelId: `Configuration already exists for this model in ${locationLabel}. Choose a different hub or edit the existing config.`,
      }));
      return;
    }

    setFormErrors((prev) => ({ ...prev, vehicleModelId: '' }));
  };


  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
        const token = localStorage.getItem("authToken");
      const params: any = {
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
      };

      if (filterCity) {
        const selectedCity = cities.find((city) => city.id === filterCity);
        const filterCityName = selectedCity?.displayName || selectedCity?.name;
        if (filterCityName) {
          params.city = filterCityName;
        }
      }
      if (filterModel) {
        params.vehicleModelId = filterModel;
      }

      const response = await axios.get(`${API_BASE_URL}/v1/security-deposit/configs`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const fetchedConfigs: SDConfig[] = response.data.data || [];
      const total = response.data.total ?? response.data.pagination?.total ?? fetchedConfigs.length;

      setConfigs(fetchedConfigs);
      setTotalCount(total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch SD configurations');
      console.error('Error fetching configs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config?: SDConfig) => {
    if (config) {
      setEditingConfig(config);
      // Find the cityId from the cities array based on city name
      const selectedCity = cities.find(c => 
        c.displayName === config.city || c.name === config.city
      );
      const cityId = selectedCity?.id || '';
      
      setFormData({
        vehicleModelId: config.vehicleModelId,
        modelName: config.modelName,
        brandName: config.brandName,
        category: config.category || '',
        batteryConfig: config.batteryConfig || '',
        cityId: cityId,
        city: config.city || '',
        hubName: config.hubName || '',
        fixedDailyRent: config.fixedDailyRent || 0,
        reductionPercentagePerYear: config.reductionPercentagePerYear || 0.05,
        baseSecurityDeposit: config.baseSecurityDeposit,
        minimumSecurityDeposit: config.minimumSecurityDeposit,
        depreciationPerMonth: config.depreciationPerMonth,
        maxDepreciationPercent: config.maxDepreciationPercent,
        useMultiplier: config.useMultiplier,
        rentalCostMultiplier: config.rentalCostMultiplier,
        notes: config.notes || '',
        imageUrl: config.imageUrl || '',
      });
    } else {
      setEditingConfig(null);
      setFormData({
        vehicleModelId: '',
        modelName: '',
        brandName: '',
        category: '',
        batteryConfig: '',
        cityId: '',
        city: '',
        hubName: '',
        fixedDailyRent: 0,
        reductionPercentagePerYear: 0.05,
        baseSecurityDeposit: 10000,
        minimumSecurityDeposit: 5000,
        depreciationPerMonth: 0.02,
        maxDepreciationPercent: 0.30,
        useMultiplier: true,
        rentalCostMultiplier: 2.0,
        notes: '',
        imageUrl: '',
      });
    }
    setOpenDialog(true);
  };

  const handleVehicleModelChange = (modelId: string) => {
    const selectedModel = vehicleModels.find(m => m.id === modelId);

    checkDuplicateConfig(
      modelId,
      formData.city,
      formData.hubName,
      formData.batteryConfig,
      editingConfig?.id
    );

    if (selectedModel) {
      setFormData({
        ...formData,
        vehicleModelId: selectedModel.id,
        modelName: selectedModel.name,
        brandName: selectedModel.oem?.displayName || selectedModel.oem?.name || '',
        category: selectedModel.category || '',
        imageUrl: formData.imageUrl || '',
      });
    } else {
      setFormData({
        ...formData,
        vehicleModelId: modelId,
        imageUrl: formData.imageUrl || '',
      });
    }
  };

  const handleCityChange = (selectedCityId: string) => {
    // Find the city object to get the city name
    const selectedCity = cities.find(c => c.id === selectedCityId);
    const cityName = selectedCity?.displayName || selectedCity?.name || '';
    
    setFormData((prev) => ({
      ...prev,
      cityId: selectedCityId,
      city: cityName, // Store city name, not ID
      hubName: '',
      imageUrl: prev.imageUrl || '',
    }));
    setFormErrors((prev) => ({
      ...prev,
      city: '',
      vehicleModelId: '',
    }));
    checkDuplicateConfig(
      formData.vehicleModelId,
      cityName,
      '',
      formData.batteryConfig,
      editingConfig?.id
    );
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConfig(null);
    setFormErrors({
      vehicleModelId: '',
      modelName: '',
      brandName: '',
      city: '',
      hubName: '',
      baseSecurityDeposit: '',
      minimumSecurityDeposit: '',
      depreciationPerMonth: '',
      maxDepreciationPercent: '',
      rentalCostMultiplier: '',
      batteryConfig: '',
      imageUrl: '',
    });
  };

  const validateForm = (): boolean => {
    const errors = {
      vehicleModelId: '',
      modelName: '',
      brandName: '',
      city: '',
      hubName: '',
      baseSecurityDeposit: '',
      minimumSecurityDeposit: '',
      depreciationPerMonth: '',
      maxDepreciationPercent: '',
      rentalCostMultiplier: '',
      batteryConfig: '',
      imageUrl: '',
    };

    let isValid = true;

    // Vehicle Model validation
    if (!formData.vehicleModelId) {
      errors.vehicleModelId = 'Please select a vehicle model';
      isValid = false;
    }

    // Model Name validation
    if (!formData.modelName || formData.modelName.trim() === '') {
      errors.modelName = 'Model name is required';
      isValid = false;
    }

    // Brand Name validation
    if (!formData.brandName || formData.brandName.trim() === '') {
      errors.brandName = 'Brand name is required';
      isValid = false;
    }

    if (!formData.city || formData.city.trim() === '') {
      errors.city = 'City is required';
      isValid = false;
    }

    if (!formData.hubName || formData.hubName.trim() === '') {
      errors.hubName = 'Hub name is required';
      isValid = false;
    }

    // Base Security Deposit validation
    if (!formData.baseSecurityDeposit || isNaN(Number(formData.baseSecurityDeposit))) {
      errors.baseSecurityDeposit = 'Base security deposit is required';
      isValid = false;
    } else if ((formData.baseSecurityDeposit as number) <= 0) {
      errors.baseSecurityDeposit = 'Base security deposit must be greater than 0';
      isValid = false;
    }

    // Minimum Security Deposit validation
    if (!formData.minimumSecurityDeposit || isNaN(Number(formData.minimumSecurityDeposit))) {
      errors.minimumSecurityDeposit = 'Minimum security deposit is required';
      isValid = false;
    } else if ((formData.minimumSecurityDeposit as number) <= 0) {
      errors.minimumSecurityDeposit = 'Minimum security deposit must be greater than 0';
      isValid = false;
    } else if ((formData.minimumSecurityDeposit as number) > (formData.baseSecurityDeposit as number)) {
      errors.minimumSecurityDeposit = 'Minimum SD cannot be greater than base SD';
      isValid = false;
    }

    // Depreciation Per Month validation
    if (formData.depreciationPerMonth < 0) {
      errors.depreciationPerMonth = 'Depreciation cannot be negative';
      isValid = false;
    } else if (formData.depreciationPerMonth > 1) {
      errors.depreciationPerMonth = 'Depreciation cannot exceed 100% (1.0)';
      isValid = false;
    }

    // Max Depreciation Percent validation
    if (formData.maxDepreciationPercent < 0) {
      errors.maxDepreciationPercent = 'Max depreciation cannot be negative';
      isValid = false;
    } else if (formData.maxDepreciationPercent > 1) {
      errors.maxDepreciationPercent = 'Max depreciation cannot exceed 100% (1.0)';
      isValid = false;
    }

    // Rental Cost Multiplier validation (only if useMultiplier is true)
    if (formData.useMultiplier) {
      if (!formData.rentalCostMultiplier || isNaN(formData.rentalCostMultiplier)) {
        errors.rentalCostMultiplier = 'Rental cost multiplier is required';
        isValid = false;
      } else if (formData.rentalCostMultiplier <= 0) {
        errors.rentalCostMultiplier = 'Multiplier must be greater than 0';
        isValid = false;
      }
    }

    // Battery Config validation
    if (!formData.batteryConfig || formData.batteryConfig.trim() === '') {
      errors.batteryConfig = 'Battery configuration is required';
      isValid = false;
    }

    // Image URL validation
    if (!formData.imageUrl || formData.imageUrl.trim() === '') {
      errors.imageUrl = 'Image URL is required';
      isValid = false;
    } else {
      try {
        new URL(formData.imageUrl);
      } catch {
        errors.imageUrl = 'Please enter a valid URL';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSaveConfig = async () => {
    const token = localStorage.getItem("authToken");
    setError(null);

    // Validate form before submission
    if (!validateForm()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    try {
      // Remove cityId before sending to backend (only hubName is needed)
      const { cityId, ...dataToSubmit } = formData;
      console.log('Submitting form data:', dataToSubmit);
      if (editingConfig) {
        await axios.put(
          `${API_BASE_URL}/v1/security-deposit/configs/${editingConfig.id}`,
          dataToSubmit,
          {
            headers: {
                Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        await axios.post(`${API_BASE_URL}/v1/security-deposit/configs`, dataToSubmit, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
      fetchConfigs();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Full error:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);

      if (err.response?.status === 409) {
        // Conflict - configuration already exists
        const existingConfig = err.response?.data?.data;
        if (existingConfig && window.confirm(
          `A configuration already exists for this vehicle model.\n\n` +
          `Current settings:\n` +
          `Base SD: ₹${existingConfig.baseSecurityDeposit?.toLocaleString()}\n` +
          `Min SD: ₹${existingConfig.minimumSecurityDeposit?.toLocaleString()}\n\n` +
          `Would you like to edit the existing configuration instead?`
        )) {
          // Open the existing config for editing
          handleOpenDialog(existingConfig);
          return;
        }
        setError('A configuration already exists for this vehicle model. Please select a different model or edit the existing one.');
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save configuration');
      }
    }
  };

  const handleDeleteConfig = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this configuration?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/v1/security-deposit/configs/${id}`);
      fetchConfigs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete configuration');
      console.error('Error deleting config:', err);
    }
  };

  const handleCalculate = async () => {
    if (!calculatorData.vehicleModelId) {
      alert('Please select a vehicle model');
      return;
    }

    // Find the selected config to get the rental rate
    const selectedConfig = configs.find(c => c.vehicleModelId === calculatorData.vehicleModelId);
    if (!selectedConfig) {
      alert('Configuration not found for selected model');
      return;
    }

    // Calculate age-based reduction using custom percentage
    const vehicleAgeInYears = calculatorData.vehicleAge;
    const reductionPercentageDecimal = (calculatorData.reductionPercentage / 100) * vehicleAgeInYears;
    const cappedReduction = Math.min(
      reductionPercentageDecimal,
      selectedConfig.maxDepreciationPercent || 0.15
    );

    const baseRental = selectedConfig.fixedDailyRent || 0;
    const reductionAmount = baseRental * cappedReduction;
    const finalRental = baseRental - reductionAmount;

    // Set the calculation result directly (no API call needed for simple calculation)
    setCalculation({
      baseSecurityDeposit: baseRental,
      depreciationAmount: reductionAmount,
      depreciationPercentage: cappedReduction * 100,
      calculatedSD: finalRental,
      minimumSD: selectedConfig.minimumSecurityDeposit || 0,
      finalSD: finalRental,
      calculationMethod: `${vehicleAgeInYears} year${vehicleAgeInYears !== 1 ? 's' : ''} × ${calculatorData.reductionPercentage}%/year = ${(cappedReduction * 100).toFixed(1)}% reduction`,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Security Deposit Configuration
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<CalculateIcon />}
            onClick={() => setOpenCalculator(true)}
            sx={{ mr: 2 }}
          >
            Rental Calculator
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Configuration
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="bold">
          New Simplified Pricing Model
        </Typography>
        <Typography variant="body2">
          • <strong>Fixed Daily Rent:</strong> Base rental rate per OEM+City (e.g., BGauss/Bengaluru: ₹200/day)
        </Typography>
        <Typography variant="body2">
          • <strong>Age-Based Reduction:</strong> Configurable % per year applied to rental only. Example: 5%/year → 3-year-old vehicle = 15% discount
        </Typography>
        <Typography variant="body2">
          • <strong>Security Deposit:</strong> Fixed amount per OEM+City (no age-based reduction applied)
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth>
                <InputLabel>Filter by City</InputLabel>
                <Select
                  value={filterCity}
                  label="Filter by City"
                  onChange={(e) => {
                    setFilterCity(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {Array.isArray(cities) &&
  cities.map((city) => (
    <MenuItem key={city.id} value={city.id}>
  {city.displayName || city.name}
</MenuItem>
  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth>
                <InputLabel>Filter by Model</InputLabel>
                <Select
                  value={filterModel}
                  label="Filter by Model"
                  onChange={(e) => {
                    setFilterModel(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All Models</MenuItem>
                  {Array.isArray(vehicleModels) &&
  vehicleModels.map((model) => (
    <MenuItem key={model.id} value={model.id}>
      {model.oem?.displayName || model.oem?.name}{" "}
      {model.displayName || model.name}
    </MenuItem>
  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setFilterCity('');
                  setFilterModel('');
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vehicle Model</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Battery Config</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Hub</TableCell>
                <TableCell align="right">Fixed Daily Rent</TableCell>
                <TableCell align="center">Age Reduction</TableCell>
                <TableCell align="right">Security Deposit</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {config.modelName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {config.category || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {config.brandName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {config.batteryConfig ? (
                      <Chip label={config.batteryConfig} size="small" color="secondary" />
                    ) : (
                      <Typography variant="caption" color="textSecondary">Not set</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {config.city ? (
                      <Chip label={config.city} size="small" color="primary" />
                    ) : (
                      <Typography variant="caption" color="textSecondary">Not set</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {config.hubName ? (
                      <Chip label={config.hubName} size="small" color="secondary" />
                    ) : (
                      <Typography variant="caption" color="textSecondary">Not set</Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    {config.fixedDailyRent ? (
                      <>
                        <Typography variant="body2" fontWeight="bold">
                          ₹{config.fixedDailyRent.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Base rate
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Not configured
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${(config.reductionPercentagePerYear * 100).toFixed(0)}%/year`}
                      size="small"
                      color="warning"
                    />
                    <Typography variant="caption" color="textSecondary" display="block">
                      Max: {(config.maxDepreciationPercent * 100).toFixed(0)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      ₹{config.baseSecurityDeposit.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Fixed (no age reduction)
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={config.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={config.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(config)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteConfig(config.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Config Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingConfig ? 'Edit Configuration' : 'Add Configuration'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2">
              <strong>Simplified Pricing:</strong> Set fixed rates per OEM+City. Age reduction applies to rental only.
            </Typography>
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required disabled={!!editingConfig} error={!!formErrors.vehicleModelId}>
                <InputLabel>Vehicle Model</InputLabel>
                <Select
                  value={formData.vehicleModelId}
                  label="Vehicle Model"
                  onChange={(e) => handleVehicleModelChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a vehicle model</em>
                  </MenuItem>
                  {loadingModels ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading models...
                    </MenuItem>
                  ) : (
                    vehicleModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.oem?.displayName || model.oem?.name} {model.name}
                        {model.category && ` - ${model.category}`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              {formErrors.vehicleModelId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {formErrors.vehicleModelId}
                </Typography>
              )}
              {!loadingModels && vehicleModels.length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  No vehicle models available. Please add models in Vehicle Management first.
                </Typography>
              )}
            </Grid>

            {/* New Simplified Pricing Fields - Prioritized */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1 }}>
                📍 Location-Specific Pricing (New)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.city}>
                <InputLabel>City *</InputLabel>
                <Select
                  value={formData.cityId}
                  label="City *"
                  onChange={(e) => {
                    const cityId = e.target.value;
                    console.log('Selected cityId:', cityId);
                    handleCityChange(cityId);
                  }}
                >
                  <MenuItem value="">Select a city</MenuItem>
                  {loadingCities ? (
                    <MenuItem disabled>Loading cities...</MenuItem>
                  ) : cities.length > 0 ? (
                    cities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
  {city.displayName || city.name}
</MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No cities available</MenuItem>
                  )}
                </Select>
                {formErrors.city && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formErrors.city}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.hubName}>
                <InputLabel>Hub Name *</InputLabel>
                <Select
                  value={formData.hubName}
                  label="Hub Name *"
                  onChange={(e) => {
                    const hubName = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      hubName,
                    }));
                    setFormErrors((prev) => ({
                      ...prev,
                      hubName: '',
                      vehicleModelId: '',
                    }));
                    checkDuplicateConfig(
                      formData.vehicleModelId,
                      formData.city,
                      hubName,
                      formData.batteryConfig,
                      editingConfig?.id,
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Select a hub</em>
                  </MenuItem>
                  <MenuItem value="Begumpet Warehouse">Begumpet Warehouse</MenuItem>
                  <MenuItem value="Begur Warehouse">Begur Warehouse</MenuItem>
                  <MenuItem value="Chattarpur Warehouse">Chattarpur Warehouse</MenuItem>
                  <MenuItem value="Jakkur Warehouse">Jakkur Warehouse</MenuItem>
                  <MenuItem value="Kovilambakkam Warehouse">Kovilambakkam Warehouse</MenuItem>
                  <MenuItem value="Manpada Thana Warehouse">Manpada Thana Warehouse</MenuItem>
                  <MenuItem value="Saibaba Colony Warehouse">Saibaba Colony Warehouse</MenuItem>
                  <MenuItem value="Sector 156 Warehouse">Sector 156 Warehouse</MenuItem>
                  <MenuItem value="Vijayaya Nagar Warehouse">Vijayaya Nagar Warehouse</MenuItem>
                </Select>
                {formErrors.hubName && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formErrors.hubName}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Age-Based Reduction % per Year *"
                value={formData.reductionPercentagePerYear !== null
                  ? (formData.reductionPercentagePerYear * 100).toFixed(0)
                  : '5'}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setFormData({ ...formData, reductionPercentagePerYear: 0.05 });
                  } else {
                    const parsed = parseFloat(val);
                    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
                      // Convert percentage to decimal (e.g., 5 -> 0.05)
                      setFormData({ ...formData, reductionPercentagePerYear: parsed / 100 });
                    }
                  }
                }}
                required
                InputProps={{
                  endAdornment: <InputAdornment position="end">% per year</InputAdornment>,
                }}
                helperText="E.g., 5 means: 1yr=5% off, 2yr=10% off, 3yr=15% off"
                placeholder="5"
                inputProps={{ min: 0, max: 100, step: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Fixed Daily Rent *"
                value={formData.fixedDailyRent || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setFormData({ ...formData, fixedDailyRent: '' as any });
                  } else {
                    const parsed = parseFloat(val);
                    if (!isNaN(parsed) && parsed >= 0) {
                      setFormData({ ...formData, fixedDailyRent: parsed });
                    }
                  }
                }}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Base daily rental rate before age reduction (e.g., 200, 214, 240)"
                placeholder="214"
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Security Deposit (Fixed) *"
                value={formData.baseSecurityDeposit || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setFormData({ ...formData, baseSecurityDeposit: '' as any });
                  } else {
                    const parsed = parseFloat(val);
                    if (!isNaN(parsed) && parsed >= 0) {
                      setFormData({ ...formData, baseSecurityDeposit: parsed });
                    }
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                error={!!formErrors.baseSecurityDeposit}
                helperText={formErrors.baseSecurityDeposit || "Fixed amount, no age reduction (e.g., 2000, 2500)"}
                required
                placeholder="2000"
                inputProps={{ min: 0, step: 100 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Minimum SD"
                value={formData.minimumSecurityDeposit || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setFormData({ ...formData, minimumSecurityDeposit: '' as any });
                  } else {
                    const parsed = parseFloat(val);
                    if (!isNaN(parsed) && parsed >= 0) {
                      setFormData({ ...formData, minimumSecurityDeposit: parsed });
                    }
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                error={!!formErrors.minimumSecurityDeposit}
                helperText={formErrors.minimumSecurityDeposit || "Usually same as base SD"}
                required
                placeholder="2000"
                inputProps={{ min: 0, step: 100 }}
              />
            </Grid>

            {/* Vehicle Details - Auto-filled */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                🚗 Vehicle Details (Auto-filled from model)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Model Name"
                value={formData.modelName}
                onChange={(e) => setFormData({ ...formData, modelName: e.target.value })}
                required
                disabled={!!formData.vehicleModelId && vehicleModels.some(m => m.id === formData.vehicleModelId)}
                error={!!formErrors.modelName}
                helperText={formErrors.modelName || "Auto-filled"}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Brand Name"
                value={formData.brandName}
                onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
                required
                disabled={!!formData.vehicleModelId && vehicleModels.some(m => m.id === formData.vehicleModelId)}
                error={!!formErrors.brandName}
                helperText={formErrors.brandName || "Auto-filled"}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                disabled={!!formData.vehicleModelId && vehicleModels.some(m => m.id === formData.vehicleModelId)}
                helperText="Auto-filled"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth error={!!formErrors.batteryConfig}>
                <InputLabel>Battery Config *</InputLabel>
                <Select
                  value={formData.batteryConfig}
                  required
                  label="Battery Config *"
                  onChange={(e) => setFormData({ ...formData, batteryConfig: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Select battery config</em>
                  </MenuItem>
                  <MenuItem value="SWAPPING">Swapping</MenuItem>
                  <MenuItem value="SINGLE">Single</MenuItem>
                </Select>
                {formErrors.batteryConfig && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formErrors.batteryConfig}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes about this configuration"
              />
            </Grid>

            {/* Image URL */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL *"
                type="url"
                value={formData.imageUrl}
                onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                placeholder="https://example.com/image.jpg"
                required
                error={!!formErrors.imageUrl}
                helperText={formErrors.imageUrl || "URL to vehicle model image or configuration image"}
              />
            </Grid>

            {/* Image Preview */}
            {formData.imageUrl && (
              <Grid item xs={12}>
                <Box
                  component="img"
                  src={formData.imageUrl}
                  alt="Configuration preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 1,
                    border: '1px solid #ddd',
                    objectFit: 'cover',
                  }}
                  onError={( ) => console.log('Image failed to load')}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveConfig} variant="contained">
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calculator Dialog */}
      <Dialog open={openCalculator} onClose={() => setOpenCalculator(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rental Calculator</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2">
              Calculate daily rental based on vehicle age and custom reduction rate.
            </Typography>
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label=""
                value={calculatorData.vehicleModelId}
                onChange={(e) => {
                  const selectedConfig = configs.find(c => c.vehicleModelId === e.target.value);
                  setCalculatorData({
                    ...calculatorData,
                    vehicleModelId: e.target.value,
                    reductionPercentage: selectedConfig ? (selectedConfig.reductionPercentagePerYear * 100) : 5
                  });
                }}
                SelectProps={{ native: true }}
              >
                <option value="">Select a Model</option>
                {(() => {
                  const uniqueModels = new Map();
                  configs
                    .filter((c) => c.isActive && c.fixedDailyRent)
                    .forEach((config) => {
                      const key = config.vehicleModelId;
                      if (!uniqueModels.has(key)) {
                        uniqueModels.set(key, config);
                      }
                    });
                  return Array.from(uniqueModels.values()).map((config) => (
                    <option key={config.vehicleModelId} value={config.vehicleModelId}>
                      {config.brandName} {config.modelName}
                    </option>
                  ));
                })()}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Vehicle Age (years)"
                value={calculatorData.vehicleAge || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setCalculatorData({ ...calculatorData, vehicleAge: 0 });
                  } else {
                    const parsed = parseFloat(val);
                    if (!isNaN(parsed) && parsed >= 0) {
                      setCalculatorData({ ...calculatorData, vehicleAge: parsed });
                    }
                  }
                }}
                helperText="Enter vehicle age in years"
                placeholder="3"
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Reduction % per Year"
                value={calculatorData.reductionPercentage || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setCalculatorData({ ...calculatorData, reductionPercentage: 5 });
                  } else {
                    const parsed = parseFloat(val);
                    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
                      setCalculatorData({ ...calculatorData, reductionPercentage: parsed });
                    }
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%/year</InputAdornment>,
                }}
                helperText="Reduction rate per year of age"
                placeholder="5"
                inputProps={{ min: 0, max: 100, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button fullWidth variant="contained" onClick={handleCalculate}>
                Calculate Rental
              </Button>
            </Grid>

            {calculation && (
              <>
                <Grid item xs={12}>
                  <Card sx={{ bgcolor: 'primary.50', mt: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary.dark">
                        🚗 Daily Rental Amount
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Base Rent:
                          </Typography>
                          <Typography variant="h6">
                            ₹{calculation.baseSecurityDeposit.toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Age Reduction:
                          </Typography>
                          <Typography variant="h6" color="error">
                            -{calculation.depreciationPercentage.toFixed(0)}%
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="textSecondary">
                          Final Daily Rental:
                        </Typography>
                        <Typography variant="h4" color="primary.main">
                          ₹{calculation.finalSD.toLocaleString()}/day
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        Method: {calculation.calculationMethod}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCalculation(null);
            setCalculatorData({ vehicleModelId: '', vehicleAge: 0, reductionPercentage: 5 });
          }}>
            Reset
          </Button>
          <Button onClick={() => setOpenCalculator(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityDepositConfigManagement;